import { useQueryClient } from '@tanstack/vue-query';
import { match } from 'ts-pattern';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useVerifiedSupabaseUser } from '~/composables/useVerifiedSupabaseUser';
import { fetchMerchantContext, checkMerchantAccess, fetchProgrammeContext } from '~/utils/merchant';
import { getProgrammeApplicationStatus } from '~~/models/Programme';

/**
 * The "You Shall Not Pass" middleware. (Unless you apply!)
 *
 * This middleware checks if a merchant requires a default programme.
 * If the user is logged in but hasn't applied (or isn't pending),
 * we force them to the application page.
 */
export default defineNuxtRouteMiddleware(async (to) => {
	if (to.name === 'merchantSlug-programme-programmeId-apply') {
		return;
	}

	const merchantSlug = useMerchantSlug(to);

	if (!merchantSlug.value) {
		return;
	}

	const user = useVerifiedSupabaseUser();

	if (!user.value) {
		return;
	}

	const queryClient = useQueryClient();

	const { merchant, relationship } = await fetchMerchantContext({
		queryClient,
		merchantSlug: merchantSlug.value,
		user: user.value,
	});

	const checkedMerchant = checkMerchantAccess({
		isPublicPage: to.meta.isPublicMerchantPage ?? false,
		merchant,
		user: user.value,
		relationship,
	}).unwrapOr(null);

	if (!checkedMerchant || to.meta.isPublicMerchantPage) {
		return;
	}

	const defaultProgramme = await fetchProgrammeContext({
		queryClient,
		merchantId: checkedMerchant.id,
	});

	if (!defaultProgramme) {
		return;
	}

	const applicationStatus = getProgrammeApplicationStatus(defaultProgramme);

	return match(applicationStatus)
		.with('Unapproved', 'Unknown', () => navigateTo({
			name: 'merchantSlug-programme-programmeId-apply',
			params: {
				merchantSlug: merchantSlug.value!,
				programmeId: defaultProgramme.id,
			},
			query: {
				flow: 'signup',
			},
		}))
		.with('Approved', 'Pending', 'Rejected', () => {})
		.exhaustive();
});
